import { getAuthConfig } from '@avila-roffe/api-clients-admin-bff';
import { type ReactNode, useEffect, useState } from 'react';
import { SplashScreen } from '../app/components/SplashScreen';
import { TenantContext, type TenantState } from './TenantContext';
import { resolveTenantSlug } from './tenantResolver';

interface Props {
  children: ReactNode;
}

function FullScreenMessage({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4 text-center">{children}</div>
    </div>
  );
}

export function TenantBootstrapProvider({ children }: Props) {
  const [state, setState] = useState<TenantState>(() => ({
    status: 'loading',
    slug: resolveTenantSlug(),
    config: null,
    error: null,
  }));

  useEffect(() => {
    // Only fetch once — skip if already resolved
    if (state.status !== 'loading') return;

    if (!state.slug) {
      setState((s) => ({ ...s, status: 'not_found' }));
      return;
    }

    let cancelled = false;

    getAuthConfig(state.slug)
      .then((res) => {
        if (cancelled) return;
        setState((s) => ({ ...s, status: 'ready', config: res.data.data }));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const status = err instanceof Error && err.message.includes('404') ? 'not_found' : 'error';
        setState((s) => ({
          ...s,
          status,
          error: err instanceof Error ? err.message : 'Unknown error',
        }));
      });

    return () => {
      cancelled = true;
    };
  }, [state.slug, state.status]);

  if (state.status === 'loading') {
    return <SplashScreen />;
  }

  if (state.status === 'not_found') {
    return (
      <FullScreenMessage>
        <p className="text-lg font-semibold">Tenant not found</p>
        <p className="text-sm text-muted-foreground">
          {state.slug
            ? `No tenant with slug "${state.slug}" exists.`
            : 'No tenant specified. Add ?tenant=<slug> to the URL.'}
        </p>
      </FullScreenMessage>
    );
  }

  if (state.status === 'error') {
    return (
      <FullScreenMessage>
        <p className="text-lg font-semibold">Failed to load</p>
        <p className="text-sm text-muted-foreground">
          Could not reach the server. Please try again later.
        </p>
      </FullScreenMessage>
    );
  }

  return <TenantContext.Provider value={state}>{children}</TenantContext.Provider>;
}
